import React, { useState, useEffect, useRef } from 'react';
import { Tv2, Home, ChevronDown, ChevronUp } from 'lucide-react';
import { Channel } from '../types';

interface VideoPlayerProps {
  channel: Channel;
  channels: Channel[];
  onSelectChannel: (channel: Channel) => void;
  onHomeClick: () => void;
}

export function VideoPlayer({ channel, channels, onSelectChannel, onHomeClick }: VideoPlayerProps) {
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showChannels, setShowChannels] = useState(false);
  const playerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();
  const mouseYRef = useRef(0);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleMovement = (e: MouseEvent | TouchEvent) => {
      const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
      mouseYRef.current = y;
      
      // Only show controls if mouse is near the top of the player (first 150px)
      const shouldShowControls = y < 150 || showChannels;
      setShowControls(shouldShowControls);
      
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      if (!showChannels && shouldShowControls) {
        controlsTimeoutRef.current = setTimeout(() => {
          if (mouseYRef.current >= 150) {
            setShowControls(false);
          }
        }, 3000);
      }
    };

    const playerElement = playerRef.current;
    if (playerElement) {
      playerElement.addEventListener('mousemove', handleMovement);
      playerElement.addEventListener('touchstart', handleMovement);
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      if (playerElement) {
        playerElement.removeEventListener('mousemove', handleMovement);
        playerElement.removeEventListener('touchstart', handleMovement);
      }
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [showChannels]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      playerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div 
      ref={playerRef}
      className="relative h-full bg-black"
    >
      {/* Video Player */}
      <iframe
        src={`https://aloserver.vercel.app/playerwhep.html?stream=${channel.stream}`}
        className="w-full h-full"
        allowFullScreen
        style={{ pointerEvents: showControls ? 'none' : 'auto' }}
      />
      
      {/* Controls Overlay */}
      <div 
        className={`absolute inset-0 pointer-events-none transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Top Controls */}
        <div className="absolute top-0 left-0 right-0 pointer-events-auto">
          <div className="p-4 bg-gradient-to-b from-black/80 via-black/50 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onHomeClick}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Home className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <span className="text-lg">{channel.logo}</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{channel.name}</h3>
                    <p className="text-sm text-red-500 live-dot pl-3">AO VIVO</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowChannels(!showChannels)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  {showChannels ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Tv2 className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Channel Thumbnails */}
            <div className={`transition-all duration-300 ${showChannels ? 'h-32 opacity-100 mt-4' : 'h-0 opacity-0'} overflow-hidden`}>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {channels.map((ch) => (
                  <button
                    key={ch.id}
                    onClick={() => {
                      onSelectChannel(ch);
                      setShowChannels(false);
                    }}
                    className={`flex-shrink-0 group relative rounded-lg overflow-hidden 
                      ${channel.id === ch.id ? 'ring-2 ring-red-500' : ''}`}
                  >
                    <img 
                      src={ch.thumbnail} 
                      alt={ch.name}
                      className="w-40 h-24 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-sm font-medium truncate">{ch.name}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}